import * as React from 'react';

import classNames from 'classnames';

import {Button} from '../button/button';
import {RemoveButton} from '../button/remove-button/remove-button';
import {ListItem} from '../list-item/generic/list-item';
import {ListView} from '../list-view/list-view';
import {IconAndLabel} from '../select-two-line-icon/icon-and-label';
import type {TwoLineOptionWithIcon} from '../select-two-line-icon/select-two-line-icon';
import {Body} from '../typography/body';

import {getSeeMoreText, getEntityLabel} from './helpers';

import styles from './selection-modal.module.css';

type Props = {
    selectedOptions: TwoLineOptionWithIcon<any>[];
    setSelectedOptions: (options: TwoLineOptionWithIcon<any>[]) => void;
    subtitle?: string;
    nothingFoundState: string;
    isShort?: boolean;
    hasBottomBorder?: boolean;
    shouldListFitContent?: boolean;
    smallLabel?: boolean;
    canRemove?: boolean;
    maxDisplayedBeforeSeeMore?: number;
};

export function SelectionList(props: Props) {
    const {hasBottomBorder = true, canRemove = true} = props;

    const [showSeeMore, setShowSeeMore] = React.useState<boolean>(false);

    const listClassName = classNames(styles.list, {
        [styles['list--short']]: props.isShort,
        [styles['list--bottom-border']]: hasBottomBorder,
        [styles['list--fit-content']]: Boolean(props.shouldListFitContent),
    });

    const rows =
        props.maxDisplayedBeforeSeeMore && !showSeeMore
            ? props.selectedOptions.slice(0, props.maxDisplayedBeforeSeeMore)
            : props.selectedOptions;

    return (
        <>
            <div className={listClassName}>
                {props.subtitle && <Body isBold={true}>{props.subtitle}</Body>}
                <ListView
                    isFullHeight={true}
                    collection={rows}
                    isOverflowAuto={true}
                    itemSpacing={16}
                    listPadding={8}
                    specialStates={[
                        {
                            shouldRender: () => props.selectedOptions.length === 0,
                            component: (
                                <div className='flex flex-dir-col justify-center align-center full-height'>
                                    <Body size='small' color='light'>
                                        {props.nothingFoundState}
                                    </Body>
                                </div>
                            ),
                        },
                    ]}
                    renderRow={(option) => {
                        const actions = [...((option.metaData && option.metaData.actions) || [])];
                        if (canRemove) {
                            actions.push(
                                <RemoveButton
                                    key='remove'
                                    onClick={() => {
                                        const newSelectedOptions = props.selectedOptions.filter(
                                            (prevSelectedOption) =>
                                                prevSelectedOption.value !== option.value
                                        );
                                        props.setSelectedOptions(newSelectedOptions);
                                    }}
                                    className='no-padding pl-8'
                                />
                            );
                        }

                        return (
                            <ListItem
                                actions={actions}
                                justifyContent='space-between'
                                noPadding={true}
                                noActionsPadding={true}
                                renderAsDiv={true}
                            >
                                <IconAndLabel
                                    label={
                                        option.label || `Unnamed ${getEntityLabel(option.value)}`
                                    }
                                    sublabel={option.sublabel}
                                    icon={option.icon}
                                    iconNode={option.iconNode}
                                    // @ts-expect-error
                                    avatar={option.avatar}
                                    smallLabel={props.smallLabel}
                                    shouldWrap={false}
                                    href={option.href}
                                />
                            </ListItem>
                        );
                    }}
                />
            </div>
            {props.maxDisplayedBeforeSeeMore &&
                props.selectedOptions.length > props.maxDisplayedBeforeSeeMore &&
                !showSeeMore && (
                    <Button
                        variant='text-primary'
                        onClick={() => setShowSeeMore(true)}
                        noPadding={true}
                        isFullWidth={true}
                    >
                        <div className='flex justify-center'>
                            {getSeeMoreText(
                                props.selectedOptions.length,
                                props.maxDisplayedBeforeSeeMore
                            )}
                        </div>
                    </Button>
                )}
            {showSeeMore && (
                <Button
                    variant='text-primary'
                    onClick={() => setShowSeeMore(false)}
                    noPadding={true}
                    isFullWidth={true}
                >
                    <div className='flex justify-center'>Show less</div>
                </Button>
            )}
        </>
    );
}
